import { useState, useCallback, useRef } from 'react';
import { apiRequest } from '../utils/api';
import { clearAnalysisFilters } from '../components2/analysis/utils/analysisFilters';
import useHistoricalDataPolling from './useHistoricalDataPolling';

/**
 * Hook for managing progressive analysis execution using the new clean pipeline
 * Handles both summary (immediate) and full (background) analysis flows
 * 
 * Separate from useAnalysisState - this handles the PROCESS, not the STATE
 */
export function useRunNewAnalysis() {
  // 🔄 PROGRESSIVE ANALYSIS STATE
  const [progressiveState, setProgressiveState] = useState({
    isProgressive: false,
    stage: 'idle', // 'idle' | 'summary' | 'full' | 'polling' | 'complete' | 'error'
    progress: 0,
    message: '',
    error: null,
    summaryComplete: false,
    fullAnalysisComplete: false,
    shouldNavigate: false, // Flag to trigger navigation after analysis completion
  });

  // 📊 ANALYSIS RESULT STATE
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // 🔄 POLLING CONTROL
  const pollingIntervalRef = useRef(null);
  const taskIdRef = useRef(null);

  // 🎯 HISTORICAL DATA POLLING for onboarding enhancement
  const historicalDataPolling = useHistoricalDataPolling();

  // 🧹 CLEANUP FUNCTION
  const cleanup = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    taskIdRef.current = null;
    // Also stop historical data polling when cleaning up
    historicalDataPolling.stopPolling();
  }, [historicalDataPolling]);

  // 🗑️ CLEAR SUBSCRIPTION BANNER DISMISSALS
  const clearSubscriptionBannerDismissals = useCallback(() => {
    // Clear all subscription banner dismissal entries from localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('subscription-banner-dismissed-')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }, []);

  // 📊 UPDATE PROGRESSIVE STATE HELPER
  const updateProgressiveState = useCallback((updates) => {
    setProgressiveState(prev => ({ ...prev, ...updates }));
  }, []);

  // 🔄 POLLING FUNCTION FOR FULL ANALYSIS
  const pollForFullAnalysis = useCallback(async (taskId) => {
    updateProgressiveState({
      stage: 'polling',
      progress: 50,
      message: 'Processing full analysis with keywords...',
    });

    let pollCount = 0;
    const maxPolls = 60 // Maximum 3 minutes of polling (60 * 3 seconds)

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      
      // Safety check: Stop polling after maximum attempts
      if (pollCount > maxPolls) {
        cleanup();
        updateProgressiveState({
          stage: 'timeout',
          error: 'Our servers are busy right now. You have the summary analysis to review, and the full analysis with keywords will be cached automatically in the background. Feel free to browse your existing analyses - when you return later, the complete analysis will be available instantly!',
          isProgressive: false,
          shouldNavigate: false,
        });
        return;
      }
      try {
        const statusResponse = await apiRequest(`/api/analyze/v2/status/${taskId}/`, {
          method: 'GET',
        });

        if (!statusResponse.ok) {
          throw new Error(`Status check failed with status ${statusResponse.status}`);
        }

        const response = await statusResponse.json();

        if (response.status === 'completed') {
          // Clear polling
          cleanup();
          
          // The full analysis is in response.results
          const fullAnalysis = response.results;
          
          // 🎯 ONBOARDING ENHANCEMENT: Start silent historical data polling if available
          if (response.historical_data?.task_id) {
            console.log('🎯 [Analysis] Starting silent historical data polling:', response.historical_data.task_id);
            historicalDataPolling.startSilentPolling(response.historical_data.task_id);
          }
          
          // Update state
          setAnalysisResult(fullAnalysis);
          updateProgressiveState({
            stage: 'complete',
            progress: 100,
            message: 'Full analysis complete with keywords!',
            fullAnalysisComplete: true,
            isProgressive: false,
            shouldNavigate: false, // Don't navigate again - already navigated with summary
          });
          
        } else if (response.status === 'error') {
          cleanup();
          updateProgressiveState({
            stage: 'error',
            error: response.message || response.error || 'Full analysis failed during processing',
            isProgressive: false,
            shouldNavigate: false, // Clear navigation flag on error
          });
          
        } else {
          // Still processing - update progress
          const backendProgress = response.progress || 0;
          
          if (response.status === 'queued') {
            // Task is queued (waiting for worker)
            updateProgressiveState({
              progress: 50, // Keep at 50% for queued state
              message: response.message || 'Task queued - waiting for worker...',
            });
          } else {
            // Task is processing - map backend progress (0-100) to UI progress (50-95)
            const uiProgress = Math.min(95, 50 + (backendProgress * 0.45));
            updateProgressiveState({
              progress: uiProgress,
              message: response.message || 'Processing full analysis with keywords...',
            });
          }
        }
        
      } catch (error) {
        cleanup();
        updateProgressiveState({
          stage: 'error',
          error: 'Failed to check analysis status',
          isProgressive: false,
          shouldNavigate: false, // Clear navigation flag on polling error
        });
      }
    }, 3000); // Poll every 3 seconds (more reasonable for analysis tasks)
    
  }, [cleanup, updateProgressiveState]);



  // 🚀 MAIN ANALYSIS FUNCTION (ALWAYS PROGRESSIVE)
  const runNewAnalysis = useCallback(async (formData) => {
    try {
      // Reset state - don't assume progressive until we know
      cleanup();
      setAnalysisResult(null);
      
      // Clear analysis filters since we're starting a new analysis
      clearAnalysisFilters();
      
      // Clear subscription banner dismissals so users see limits for new analysis
      clearSubscriptionBannerDismissals();
      
      updateProgressiveState({
        isProgressive: false, // Don't assume progressive yet
        stage: 'loading',
        progress: 10,
        message: 'Starting analysis...',
        error: null,
        summaryComplete: false,
        fullAnalysisComplete: false,
        shouldNavigate: false, // Reset navigation flag
      });

      // Parse the date ranges from formattedDates
      const { old_period, new_period } = formData.dates.formattedDates;
      const [oldStart, oldEnd] = old_period.split(' - ');
      const [newStart, newEnd] = new_period.split(' - ');
      
      // Prepare request data for progressive analysis
      const requestData = {
        property_url: formData.selectedProperty,
        old_start_date: oldStart,
        old_end_date: oldEnd,
        new_start_date: newStart,
        new_end_date: newEnd,
        country: formData.country || '',
        use_country_scaling: formData.useCountryScaling !== undefined ? formData.useCountryScaling : false,
        collect_historical: formData.collectHistorical !== undefined ? formData.collectHistorical : true
      };

      updateProgressiveState({
        progress: 20,
        message: 'Running summary analysis...',
      });

      const response = await apiRequest('/api/analyze/v2/run/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `Progressive analysis failed with status ${response.status}`);
      }

      const progressiveResponse = await response.json();
      
      // Check if this is a cache hit (instant complete analysis)
      if (progressiveResponse.cache_hit && progressiveResponse.analysis_type === 'cached') {
        // Set complete results immediately
        setAnalysisResult(progressiveResponse.results);
        
        updateProgressiveState({
          stage: 'complete',
          progress: 100,
          message: progressiveResponse.message || 'Analysis retrieved from cache (instant)',
          summaryComplete: true,
          fullAnalysisComplete: true,
          isProgressive: false, // Cache hit is not progressive
          shouldNavigate: true, // Trigger navigation
        });
        
        return progressiveResponse.results; // Return immediately, no polling needed
      }
      
      // Check if we got summary results immediately (cache miss, progressive flow)
      if (progressiveResponse.results) {
        // Set summary results immediately
        setAnalysisResult(progressiveResponse.results);
        
        updateProgressiveState({
          stage: 'polling',
          progress: 40,
          message: 'Summary complete! Processing full analysis with keywords...',
          summaryComplete: true,
          isProgressive: true, // Now we know it's progressive
          shouldNavigate: true, // Trigger navigation with summary
        });
      }
      
      // If we have a task_id, start polling for full analysis
      if (progressiveResponse.task_id) {
        // Store task ID for polling full analysis
        taskIdRef.current = progressiveResponse.task_id;
        
        // Start polling for full analysis completion
        await pollForFullAnalysis(progressiveResponse.task_id);
      } else {
        // No task ID means analysis is complete
        updateProgressiveState({
          stage: 'complete',
          progress: 100,
          message: 'Analysis complete!',
          fullAnalysisComplete: true,
          isProgressive: false,
        });
      }
      
      return progressiveResponse.results; // Return summary result immediately
      
    } catch (error) {
      cleanup();
      updateProgressiveState({
        stage: 'error',
        error: error.message || 'Failed to run progressive analysis',
        isProgressive: false,
        shouldNavigate: false, // Clear navigation flag on analysis error
      });
      throw error;
    }
  }, [cleanup, updateProgressiveState, pollForFullAnalysis]);

  // 🛑 CANCEL ANALYSIS
  const cancelAnalysis = useCallback(() => {
    cleanup();
    updateProgressiveState({
      isProgressive: false,
      stage: 'idle',
      progress: 0,
      message: '',
      error: null,
      summaryComplete: false,
      fullAnalysisComplete: false,
      shouldNavigate: false, // Clear navigation flag on cancel
    });
    setAnalysisResult(null);
  }, [cleanup, updateProgressiveState]);

  // 🧹 CLEANUP ON UNMOUNT
  const resetAnalysis = useCallback(() => {
    cancelAnalysis();
  }, [cancelAnalysis]);

  // 🧭 CLEAR NAVIGATION FLAG
  const clearNavigationFlag = useCallback(() => {
    updateProgressiveState({
      shouldNavigate: false,
    });
  }, [updateProgressiveState]);

  return {
    // State
    progressiveState,
    analysisResult,
    
    // Actions
    runNewAnalysis,
    cancelAnalysis,
    resetAnalysis,
    clearNavigationFlag,
    
    // Status helpers
    isRunning: progressiveState.isProgressive,
    isComplete: progressiveState.stage === 'complete',
    hasError: progressiveState.stage === 'error',
    hasTimeout: progressiveState.stage === 'timeout',
    
    // Historical data polling (for onboarding enhancement)
    historicalDataPolling,
    hasSummary: progressiveState.summaryComplete,
    hasFullAnalysis: progressiveState.fullAnalysisComplete,
  };
} 